import React from 'react';
import { Check } from '@/shared/ui/icons';
import { cn } from '../../shared/ui/utils';
import { PresenceDot } from './PresenceDot';
import type { PresenceStatus, ResolvedPresenceStatus } from '../../types/presence';
import { nextManualPresence } from '../../presence/ownPresence';
import { ACCOUNT_MENU_LABELS, ACCOUNT_STATUS_CHOICES } from '../../shared-ui/navigation/accountMenu';

/** One row of the account menu, sized and coloured from the shared --menu-* tokens. */
export const ACCOUNT_MENU_ROW = 'flex w-full min-h-[var(--menu-item-height)] items-center gap-[var(--menu-item-gap)] rounded-[var(--ui-menu-item-radius)] px-2.5 text-left text-[length:var(--menu-font-size)] text-popover-foreground transition-colors hover:bg-accent focus-visible:bg-accent outline-hidden';

interface PresenceStatusPickerProps {
  currentStatus: ResolvedPresenceStatus;
  /** 'manual' means the user explicitly set this status. */
  source: 'auto' | 'manual';
  /**
   * Called with the chosen status, or null to clear a manual override
   * and return to automatic detection.
   */
  onSelect: (status: PresenceStatus | null) => void;
  isShortViewport?: boolean;
}

export function PresenceStatusPicker({
  currentStatus,
  source,
  onSelect,
}: PresenceStatusPickerProps) {
  return (
    <div>
      <p className="px-2.5 pt-1 pb-1 text-xs text-muted-foreground">{ACCOUNT_MENU_LABELS.setStatus}</p>

      {ACCOUNT_STATUS_CHOICES.map(({ id: s, label }) => {
        const isActive = currentStatus === s;
        return (
          <button
            key={s}
            onClick={() => onSelect(nextManualPresence(s, currentStatus, source))}
            className={cn(ACCOUNT_MENU_ROW, isActive && 'bg-accent')}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              <PresenceDot status={s} size={9} />
            </span>
            <span className="flex-1">{label}</span>
            {isActive && <Check className="size-4 shrink-0 text-muted-foreground" />}
          </button>
        );
      })}

      {source === 'manual' && (
        <button onClick={() => onSelect(null)} className={cn(ACCOUNT_MENU_ROW, 'text-muted-foreground')}>
          <span className="size-4 shrink-0" />
          {ACCOUNT_MENU_LABELS.resetStatus}
        </button>
      )}
    </div>
  );
}
